import { createModel } from '@rematch/core';
import { selectors } from './selector';
import { RootState } from '../../store';
import { Message } from '../../models/message';
import { storeByIMPSId } from '../../utils/converter';
import {
    getMessages,
    getRootMessages,
    getUnreadMessages
} from '../../apis/messaging-msg';

export interface State {
    userIds: string[];
    allIds: number[];
    byId: Dictionary<Message>;
    loading: Dictionary<boolean>;
}

export const messages = createModel<State>({
    state: {
        userIds: [],
        allIds: [],
        byId: {},
        loading: {}
    },
    reducers: {
        update: (
            state: State,
            data: MixType<Message>,
            loadingKey?: string
        ): State => {
            const hasData = Array.isArray(data) ? data.length > 0 : data;
            if (hasData || loadingKey) {
                const byId = {
                    ...state.byId,
                    ...storeByIMPSId(data)
                };
                return {
                    ...state,
                    userIds: groupByUserId(byId),
                    allIds: Object
                        .keys(byId)
                        .sort((a, b) => byId[a]!.createdDt > byId[b]!.createdDt ? 1 : -1)
                        .map(x => +x),
                    byId: byId,
                    ...loadingKey && {
                        loading: {
                            ...state.loading,
                            [loadingKey]: false
                        }
                    }
                };
            } else {
                return state;
            }
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        })
    },
    effects: () => ({
        async getMessagesAsync(
            payload: {
                rootMessageId?: number,
                time?: Undefinable<string>
            },
            rootState: RootState
        ) {
            if (!selectors.getLoading(rootState.messages, 'getMessagesAsync')) {
                if (!payload.time) {
                    this.loading('getMessagesAsync', true);
                }
                try {
                    const data = await getMessages(payload.rootMessageId, payload.time);
                    this.update(data, !payload.time && 'getMessagesAsync');
                } catch (e) {
                    window.console.log(e);
                    if (!payload.time) {
                        this.loading('getMessagesAsync', false);
                    }
                }
            }
        },
        async getUnreadMessagesAsync() {
            try {
                const data = await getUnreadMessages();
                this.update(data);
            } catch (e) {
                window.console.log(e);
            }
        },
        async getRootMessagesAsync() {
            this.loading('getRootMessagesAsync', true);
            try {
                const data = await getRootMessages();
                this.update(data, 'getRootMessagesAsync');
            } catch (e) {
                window.console.log(e);
                this.loading('getRootMessagesAsync', false);
            }
        }
    })
});

export const groupByUserId = (byId: Dictionary<Message>): string[] => {
    const data = Object.keys(byId).reduce(
        (prevValue, curr) => {
            const belongToUserId = byId[curr]!.belongToUserId || 'system';
            if (!prevValue[belongToUserId] || prevValue[belongToUserId]! < byId[curr]!.createdDt) {
                prevValue[belongToUserId] = byId[curr]!.createdDt;
            }
            return prevValue;
        },
        {} as Dictionary<string>
    );
    return Object.keys(data).sort((a, b) => data[a]! < data[b]! ? 1 : -1);
};
